/**
 * Utility formatters for Amtrend Command Center
 */

import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns'

/**
 * Format a number as currency (USD)
 * @param {number} num - The number to format
 * @param {boolean} showCents - Whether to show cents (default: true)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(num, showCents = true) {
  if (num === null || num === undefined || isNaN(num)) {
    return '$0.00'
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  })

  return formatter.format(num)
}

/**
 * Format a number as compact currency (e.g., $1.5M)
 * @param {number} num - The number to format
 * @returns {string} Formatted compact currency string
 */
export function formatCurrencyCompact(num) {
  if (num === null || num === undefined || isNaN(num)) {
    return '$0'
  }

  const absNum = Math.abs(num)
  const sign = num < 0 ? '-' : ''

  if (absNum >= 1000000) {
    return `${sign}$${(absNum / 1000000).toFixed(1)}M`
  }
  if (absNum >= 1000) {
    return `${sign}$${(absNum / 1000).toFixed(1)}K`
  }
  return formatCurrency(num, false)
}

/**
 * Format a number as a percentage
 * @param {number} num - The decimal number (e.g., 0.15 for 15%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export function formatPercent(num, decimals = 1) {
  if (num === null || num === undefined || isNaN(num)) {
    return '0%'
  }

  // If the number is already in percentage form (e.g., 15 instead of 0.15)
  const value = Math.abs(num) > 1 && Math.abs(num) <= 100 ? num : num * 100

  return `${value >= 0 ? '' : '-'}${Math.abs(value).toFixed(decimals)}%`
}

/**
 * Format a date
 * @param {string|Date} date - The date to format
 * @param {string} formatStr - The format string (default: 'MMM d, yyyy')
 * @returns {string} Formatted date string
 */
export function formatDate(date, formatStr = 'MMM d, yyyy') {
  if (!date) return '-'

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (!isValid(dateObj)) return '-'
    return format(dateObj, formatStr)
  } catch (error) {
    return '-'
  }
}

/**
 * Format a date as a short date (e.g., "Jan 15")
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted short date string
 */
export function formatDateShort(date) {
  return formatDate(date, 'MMM d')
}

/**
 * Format a date with time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(date) {
  return formatDate(date, 'MMM d, yyyy h:mm a')
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time string
 */
export function formatTimeAgo(date) {
  if (!date) return '-'

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (!isValid(dateObj)) return '-'
    return formatDistanceToNow(dateObj, { addSuffix: true })
  } catch (error) {
    return '-'
  }
}

// Alias for formatTimeAgo
export const formatDaysAgo = formatTimeAgo

/**
 * Format a job number with line and release
 * @param {string|number} orderNum - The order number
 * @param {string|number} line - The line number
 * @param {string|number} release - The release number
 * @returns {string} Formatted job number
 */
export function formatJobNum(orderNum, line, release) {
  const parts = [orderNum]
  if (line !== null && line !== undefined) {
    parts.push(String(line).padStart(2, '0'))
  }
  if (release !== null && release !== undefined) {
    parts.push(String(release).padStart(2, '0'))
  }
  return parts.join('-')
}

/**
 * Format a number with thousands separators
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export function formatNumber(num, decimals = 0) {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

/**
 * Format a phone number
 * @param {string} phone - The phone number to format
 * @returns {string} Formatted phone number
 */
export function formatPhone(phone) {
  if (!phone) return '-'

  const cleaned = phone.replace(/\D/g, '')

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }

  return phone
}

/**
 * Truncate text with ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated text
 */
export function truncate(text, maxLength = 50) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Format days until due (for aging)
 * @param {number} days - Days overdue (negative for past due)
 * @returns {object} Object with text and color class
 */
export function formatDaysUntil(days) {
  if (days === null || days === undefined) {
    return { text: '-', color: 'text-gray-500' }
  }

  if (days < 0) {
    return {
      text: `${Math.abs(days)} days past due`,
      color: 'text-red-600'
    }
  }
  if (days === 0) {
    return { text: 'Due today', color: 'text-yellow-600' }
  }
  if (days <= 7) {
    return { text: `Due in ${days} days`, color: 'text-yellow-600' }
  }
  return { text: `Due in ${days} days`, color: 'text-green-600' }
}

/**
 * Get status color class
 * @param {string} status - The status string
 * @returns {string} Tailwind color class
 */
export function getStatusColor(status) {
  const statusLower = (status || '').toLowerCase()

  const colors = {
    active: 'text-green-600 bg-green-100',
    open: 'text-green-600 bg-green-100',
    complete: 'text-blue-600 bg-blue-100',
    completed: 'text-blue-600 bg-blue-100',
    closed: 'text-gray-600 bg-gray-100',
    pending: 'text-yellow-600 bg-yellow-100',
    hold: 'text-red-600 bg-red-100',
    on_hold: 'text-red-600 bg-red-100',
    cancelled: 'text-gray-400 bg-gray-100',
    expired: 'text-red-600 bg-red-100',
    overdue: 'text-red-600 bg-red-100',
    warning: 'text-yellow-600 bg-yellow-100',
    late: 'text-red-600 bg-red-100',
    released: 'text-green-600 bg-green-100',
    planned: 'text-blue-600 bg-blue-100',
  }

  return colors[statusLower] || 'text-gray-600 bg-gray-100'
}

/**
 * Format aging bucket
 * @param {string} bucket - The aging bucket (e.g., "current", "1-30", "31-60")
 * @returns {object} Object with label and color
 */
export function formatAgingBucket(bucket) {
  const buckets = {
    current: { label: 'Current', color: 'bg-green-500' },
    '1-30': { label: '1-30 Days', color: 'bg-yellow-500' },
    '31-60': { label: '31-60 Days', color: 'bg-orange-500' },
    '61-90': { label: '61-90 Days', color: 'bg-red-400' },
    '91+': { label: '91+ Days', color: 'bg-red-600' },
    over90: { label: '91+ Days', color: 'bg-red-600' },
  }

  return buckets[bucket] || { label: bucket, color: 'bg-gray-500' }
}

// Export all as default object
export default {
  formatCurrency,
  formatCurrencyCompact,
  formatPercent,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatTimeAgo,
  formatJobNum,
  formatNumber,
  formatPhone,
  truncate,
  formatDaysUntil,
  getStatusColor,
  formatAgingBucket,
}
